import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
  useCallback,
  useMemo,
} from "react";
import Map, {
  Source,
  Layer,
  NavigationControl,
  ScaleControl,
  ViewStateChangeEvent,
  useMap,
} from "react-map-gl";
import MapboxDraw from "@mapbox/mapbox-gl-draw";
import "@mapbox/mapbox-gl-draw/dist/mapbox-gl-draw.css";
import { toast } from "react-hot-toast";
import {
  State,
  Coordinates,
  MapComponentRef,
  GuideLayer,
  MapStyle,
  MAP_STYLES,
  RegionCode,
} from "../types";
import { getRegionBorder } from "../data/borders";

// Mapbox access token - you'll need to get one from mapbox.com
const MAPBOX_ACCESS_TOKEN =
  process.env.REACT_APP_MAPBOX_TOKEN ||
  "pk.eyJ1IjoiZXhhbXBsZSIsImEiOiJjbGV4YW1wbGUifQ.example";

// Vibrant geography map color palette - matching template quality
const GEOGRAPHY_COLORS = [
  "#E53935", // Bright red
  "#43A047", // Bright green
  "#9C27B0", // Bright purple
  "#607D8B", // Blue-gray
  "#FF9800", // Bright orange
  "#8BC34A", // Bright light green
  "#00BCD4", // Bright cyan
  "#FFEB3B", // Bright yellow
  "#3F51B5", // Bright indigo
  "#03A9F4", // Bright blue
  "#795548", // Brown
  "#F44336", // Material red
  "#4CAF50", // Material green
  "#2196F3", // Material blue
  "#FFC107", // Material amber
  "#9E9E9E", // Material grey
  "#673AB7", // Material deep purple
  "#009688", // Material teal
  "#FF5722", // Material deep orange
  "#CDDC39", // Material lime
  "#E91E63", // Material pink
];

// Function to get a systematic unused color from the geography palette
// This ensures we cycle through colors systematically rather than pure random
const getRandomUnusedColor = (existingStates: State[]): string => {
  const usedColors = new Set(existingStates.map((state) => state.color));
  const availableColors = GEOGRAPHY_COLORS.filter(
    (color) => !usedColors.has(color)
  );

  if (availableColors.length > 0) {
    // Use the first available color to ensure systematic cycling
    // This prevents duplicates and ensures we use all colors before repeating
    return availableColors[0];
  } else {
    // If all colors are used, cycle through the palette systematically
    // Find the least recently used color to ensure even distribution
    const colorUsageCount: Record<string, number> = {};

    // Count how many times each color is used
    existingStates.forEach((state) => {
      colorUsageCount[state.color] = (colorUsageCount[state.color] || 0) + 1;
    });

    // Find the color with the lowest usage count
    let leastUsedColor = GEOGRAPHY_COLORS[0];
    let minUsage = colorUsageCount[leastUsedColor] || 0;

    GEOGRAPHY_COLORS.forEach((color) => {
      const usage = colorUsageCount[color] || 0;
      if (usage < minUsage) {
        minUsage = usage;
        leastUsedColor = color;
      }
    });

    return leastUsedColor;
  }
};

interface MapComponentProps {
  states: State[];
  guideLayers: GuideLayer[];
  drawingMode: boolean;
  selectedState: State | null;
  editingStateId: string | null;
  showEditingInstructions: boolean;
  onStateCreated: (state: State) => void;
  onStateSelected: (state: State) => void;
  onStatesEdited: (states: State[]) => void;
  onFinishEditing: () => void;
  onHideInstructions: () => void;
  showCapitals: boolean;
  showCapitalNames?: boolean;
  showStateNames?: boolean;
  showStateFill?: boolean;
  mapStyle?: MapStyle;
  onCapitalCoordinateSet?: (stateId: string, coordinates: Coordinates) => void;
  isSettingCapital: boolean;
  editingGuideLayerId: string | null;
  selectedGuideLayer?: GuideLayer | null;
  regionCode?: string; // Current region code for border display
  showBorder?: boolean; // Whether to display the region border
  customBorder?: Coordinates[][] | null; // Custom user-drawn border
  isEditingBorder?: boolean; // Whether border is being edited
  onBorderEdited?: (coordinates: Coordinates[][]) => void; // Callback when border is edited
  onGuideLayerTransform?: (
    layerId: string,
    updates: Partial<GuideLayer>
  ) => void;
  setDrawingMode?: (mode: boolean) => void;
}

// Helper function to check if point is inside polygon
const isPointInPolygon = (
  point: Coordinates,
  polygon: Coordinates[]
): boolean => {
  let inside = false;
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const xi = polygon[i].lng;
    const yi = polygon[i].lat;
    const xj = polygon[j].lng;
    const yj = polygon[j].lat;

    const yiAbove = yi > point.lat;
    const yjAbove = yj > point.lat;
    const intersects = yiAbove !== yjAbove;
    const xIntersect = ((xj - xi) * (point.lat - yi)) / (yj - yi) + xi;

    if (intersects && point.lng < xIntersect) {
      inside = !inside;
    }
  }
  return inside;
};

// Calculate centroid of polygon
const calculateCentroid = (coordinates: Coordinates[][]): Coordinates => {
  if (!coordinates || coordinates.length === 0 || coordinates[0].length === 0) {
    return { lat: 32.5, lng: 54.0 };
  }

  const points = coordinates[0];
  let lat = 0;
  let lng = 0;

  points.forEach((point) => {
    lat += point.lat;
    lng += point.lng;
  });

  return {
    lat: lat / points.length,
    lng: lng / points.length,
  };
};

// Drawing Controls Component
const DrawingControls: React.FC<{
  drawingMode: boolean;
  editingStateId: string | null;
  states: State[];
  onStateCreated: (state: State) => void;
  onStatesEdited: (states: State[]) => void;
  onFinishEditing: () => void;
  isEditingBorder?: boolean;
  customBorder?: Coordinates[][] | null;
  onBorderEdited?: (coordinates: Coordinates[][]) => void;
}> = ({
  drawingMode,
  editingStateId,
  states,
  onStateCreated,
  onStatesEdited,
  onFinishEditing,
  isEditingBorder,
  customBorder,
  onBorderEdited,
}) => {
  const { current: map } = useMap();
  const drawRef = useRef<MapboxDraw | null>(null);
  const pulseIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Add global error handler for MapboxDraw
  useEffect(() => {
    const handleGlobalError = (event: ErrorEvent) => {
      if (
        event.error &&
        event.error.message &&
        event.error.message.includes("feature is undefined")
      ) {
        console.error("MapboxDraw feature error caught:", event.error);
        event.preventDefault();
        toast.error("Drawing error occurred. Please try again.");
      }
    };

    window.addEventListener("error", handleGlobalError);
    return () => {
      window.removeEventListener("error", handleGlobalError);
    };
  }, []);

  // Cleanup pulse interval when editing mode ends or component unmounts
  useEffect(() => {
    return () => {
      if (pulseIntervalRef.current) {
        clearInterval(pulseIntervalRef.current);
        pulseIntervalRef.current = null;
      }
    };
  }, [editingStateId]);

  useEffect(() => {
    if (!map) return undefined;

    // Initialize MapboxDraw with enhanced editing capabilities
    if (!drawRef.current) {
      try {
        drawRef.current = new MapboxDraw({
          displayControlsDefault: false,
          controls: {
            polygon: drawingMode,
            trash: false,
          },
          // Enhanced styles for better editing experience
          styles: [
            // Polygon fill styles - enhanced for better UX with pulsing animation
            {
              id: "gl-draw-polygon-fill",
              type: "fill",
              filter: [
                "all",
                ["==", "$type", "Polygon"],
                ["!=", "mode", "static"],
              ],
              paint: {
                "fill-color": "#ff6b35",
                "fill-outline-color": "#ff6b35",
                "fill-opacity": 0.3, // Match the opacity of normal states
              },
            },
            // Polygon stroke styles - enhanced for better UX with pulsing animation
            {
              id: "gl-draw-polygon-stroke-active",
              type: "line",
              filter: [
                "all",
                ["==", "$type", "Polygon"],
                ["!=", "mode", "static"],
              ],
              paint: {
                "line-color": "#ff6b35",
                "line-width": [
                  "interpolate",
                  ["linear"],
                  ["zoom"],
                  0,
                  2,
                  22,
                  4,
                ],
                "line-dasharray": [2, 2],
                "line-opacity": [
                  "interpolate",
                  ["linear"],
                  ["zoom"],
                  0,
                  0.7,
                  22,
                  1,
                ],
              },
            },
            {
              id: "gl-draw-polygon-stroke-inactive",
              type: "line",
              filter: [
                "all",
                ["==", "$type", "Polygon"],
                ["==", "mode", "static"],
              ],
              paint: {
                "line-color": "#ff6b35",
                "line-width": 2,
                "line-dasharray": [4, 4],
              },
            },
            // Pulsing animation layer for editing polygon
            {
              id: "gl-draw-polygon-stroke-pulse",
              type: "line",
              filter: [
                "all",
                ["==", "$type", "Polygon"],
                ["!=", "mode", "static"],
              ],
              paint: {
                "line-color": "#ff6b35",
                "line-width": [
                  "interpolate",
                  ["linear"],
                  ["zoom"],
                  0,
                  4,
                  22,
                  6,
                ],
                "line-opacity": [
                  "interpolate",
                  ["linear"],
                  ["zoom"],
                  0,
                  0.3,
                  22,
                  0.5,
                ],
                "line-dasharray": [2, 2],
              },
            },
            // Enhanced vertex styles for better visibility
            {
              id: "gl-draw-polygon-and-line-vertex-halo-active",
              type: "circle",
              filter: [
                "all",
                ["==", "meta", "vertex"],
                ["==", "$type", "Point"],
                ["!=", "mode", "static"],
              ],
              paint: {
                "circle-radius": 12,
                "circle-color": "#fff",
                "circle-stroke-color": "#ff6b35",
                "circle-stroke-width": 3,
              },
            },
            // Enhanced midpoint styles
            {
              id: "gl-draw-polygon-and-line-midpoint",
              type: "circle",
              filter: [
                "all",
                ["==", "meta", "midpoint"],
                ["==", "$type", "Point"],
                ["!=", "mode", "static"],
              ],
              paint: {
                "circle-radius": 8,
                "circle-color": "#ff6b35",
                "circle-stroke-color": "#fff",
                "circle-stroke-width": 2,
              },
            },
            // Active line being drawn (preview line from last vertex to mouse)
            {
              id: "gl-draw-line-active",
              type: "line",
              filter: [
                "all",
                ["==", "$type", "LineString"],
                ["==", "active", "true"],
              ],
              paint: {
                "line-color": "#ff6b35",
                "line-width": [
                  "interpolate",
                  ["linear"],
                  ["zoom"],
                  0,
                  2,
                  22,
                  4,
                ],
                "line-dasharray": [2, 2],
              },
            },
            // Inactive line segments (completed segments)
            {
              id: "gl-draw-line-inactive",
              type: "line",
              filter: [
                "all",
                ["==", "$type", "LineString"],
                ["==", "active", "false"],
              ],
              paint: {
                "line-color": "#ff6b35",
                "line-width": [
                  "interpolate",
                  ["linear"],
                  ["zoom"],
                  0,
                  2,
                  22,
                  4,
                ],
              },
            },
            // Active vertex (current mouse position)
            {
              id: "gl-draw-polygon-and-line-vertex-active",
              type: "circle",
              filter: [
                "all",
                ["==", "meta", "vertex"],
                ["==", "$type", "Point"],
                ["!=", "mode", "static"],
              ],
              paint: {
                "circle-radius": 10,
                "circle-color": "#ff6b35",
              },
            },
            // Inactive vertices (completed vertices)
            {
              id: "gl-draw-polygon-and-line-vertex-inactive",
              type: "circle",
              filter: [
                "all",
                ["==", "meta", "vertex"],
                ["==", "$type", "Point"],
                ["==", "mode", "static"],
              ],
              paint: {
                "circle-radius": 8,
                "circle-color": "#ff6b35",
                "circle-stroke-color": "#fff",
                "circle-stroke-width": 2,
              },
            },
            // Vertex halo for better visibility
            {
              id: "gl-draw-polygon-and-line-vertex-halo-inactive",
              type: "circle",
              filter: [
                "all",
                ["==", "meta", "vertex"],
                ["==", "$type", "Point"],
                ["==", "mode", "static"],
              ],
              paint: {
                "circle-radius": 10,
                "circle-color": "#fff",
                "circle-stroke-color": "#ff6b35",
                "circle-stroke-width": 2,
              },
            },
          ],
          // Enhanced options for better editing
          touchEnabled: true,
          touchBuffer: 25,
          // Additional options to ensure drawing preview works correctly
          keybindings: true,
        });

        map.addControl(drawRef.current);
      } catch (error) {
        console.error("Error initializing MapboxDraw:", error);
        toast.error("Failed to initialize drawing tools");
      }
    }

    // Handle drawing events
    const handleDrawCreate = (e: any) => {
      if (e.features && e.features.length > 0) {
        const feature = e.features[0];
        if (feature.geometry.type === "Polygon") {
          const coordinates = feature.geometry.coordinates[0].map(
            (coord: number[]) => ({
              lng: coord[0],
              lat: coord[1],
            })
          );

          const newState: State = {
            id: Date.now().toString(),
            name: `New State ${Date.now()}`,
            coordinates: [coordinates],
            color: getRandomUnusedColor(states),
            createdAt: new Date().toISOString(),
          };

          onStateCreated(newState);

          // Remove the drawn feature from the draw tool
          drawRef.current?.delete(feature.id);
        }
      }
    };

    const handleDrawUpdate = (e: any) => {
      if (e.features && e.features.length > 0) {
        const feature = e.features[0];
        if (
          feature &&
          feature.geometry &&
          feature.geometry.type === "Polygon"
        ) {
          try {
            const coordinates = feature.geometry.coordinates[0].map(
              (coord: number[]) => ({
                lng: coord[0],
                lat: coord[1],
              })
            );

            // Check if we're editing border or state
            if (isEditingBorder && onBorderEdited) {
              // For borders, we might have multiple rings (multipolygon)
              const allRings = feature.geometry.coordinates.map(
                (ring: number[][]) =>
                  ring.map((coord: number[]) => ({
                    lng: coord[0],
                    lat: coord[1],
                  }))
              );
              onBorderEdited(allRings);
            } else if (editingStateId) {
              const updatedStates = states.map((state) =>
                state.id === editingStateId
                  ? { ...state, coordinates: [coordinates] }
                  : state
              );
              onStatesEdited(updatedStates);
            }
          } catch (error) {
            console.error("Error updating polygon:", error);
            toast.error("Failed to update polygon shape");
          }
        }
      }
    };

    const handleDrawDelete = () => {
      onFinishEditing();
    };

    // Add event listeners with error handling
    const safeAddEventListener = (event: string, handler: any) => {
      try {
        map.on(event, handler);
      } catch (error) {
        console.error(`Error adding ${event} listener:`, error);
      }
    };

    safeAddEventListener("draw.create", handleDrawCreate);
    safeAddEventListener("draw.update", handleDrawUpdate);
    safeAddEventListener("draw.delete", handleDrawDelete);

    // Update draw mode with better error handling
    if (drawingMode) {
      try {
        drawRef.current?.changeMode("draw_polygon");
      } catch (error) {
        console.error("Error entering draw mode:", error);
        toast.error("Failed to enter drawing mode");
      }
    } else if (isEditingBorder && customBorder && customBorder.length > 0) {
      // Load border for editing
      if (drawRef.current) {
        try {
          // Clear existing features
          const features = drawRef.current.getAll();
          if (features && features.features && features.features.length > 0) {
            features.features.forEach((feature: any) => {
              if (feature && feature.id) {
                try {
                  drawRef.current?.delete(feature.id);
                } catch (error) {
                  console.error("Error deleting feature:", error);
                }
              }
            });
          }

          // Add border as a feature
          const feature = {
            type: "Feature",
            properties: { id: "border-edit", type: "border" },
            geometry: {
              type: "Polygon",
              coordinates: [
                customBorder[0].map((coord) => [coord.lng, coord.lat]),
              ],
            },
          };

          drawRef.current.add(feature);
          drawRef.current.changeMode("simple_select");
          toast("💡 Click the border to start editing", { duration: 3000 });
        } catch (error) {
          console.error("Error loading border for editing:", error);
          toast.error("Failed to load border for editing");
        }
      }
    } else if (editingStateId) {
      // Add the state being edited to the draw tool for editing
      const stateToEdit = states.find((s) => s.id === editingStateId);
      if (stateToEdit && drawRef.current) {
        try {
          // Clear existing features first
          const features = drawRef.current.getAll();
          if (features && features.features && features.features.length > 0) {
            features.features.forEach((feature: any) => {
              if (feature && feature.id) {
                try {
                  drawRef.current?.delete(feature.id);
                } catch (error) {
                  console.error("Error deleting feature:", error);
                }
              }
            });
          }

          // Create the feature with validation
          if (
            stateToEdit.coordinates &&
            stateToEdit.coordinates[0] &&
            stateToEdit.coordinates[0].length >= 3
          ) {
            const feature = {
              type: "Feature",
              properties: { id: editingStateId },
              geometry: {
                type: "Polygon",
                coordinates: [
                  stateToEdit.coordinates[0].map((coord) => [
                    coord.lng,
                    coord.lat,
                  ]),
                ],
              },
            };

            // Add the feature with error handling
            try {
              drawRef.current.add(feature);
            } catch (error) {
              console.error("Error adding feature to draw tool:", error);
              toast.error("Failed to load polygon for editing");
              return;
            }

            // Wait for the feature to be added, then enter edit mode
            const enterEditMode = (featureId: string) => {
              try {
                if (drawRef.current) {
                  // Use simple_select mode - this shows the polygon with vertices
                  // User needs to click once on the polygon to make vertices editable
                  drawRef.current.changeMode("simple_select");

                  // Clear any existing pulse interval
                  if (pulseIntervalRef.current) {
                    clearInterval(pulseIntervalRef.current);
                    pulseIntervalRef.current = null;
                  }

                  // Add pulsing animation to make the polygon feel clickable
                  let pulseCount = 0;
                  pulseIntervalRef.current = setInterval(() => {
                    if (drawRef.current && editingStateId && map) {
                      try {
                        // Create a subtle pulsing effect by updating the stroke width
                        const currentWidth = 2 + Math.sin(pulseCount * 0.3) * 1;
                        const mapInstance = map.getMap();
                        if (mapInstance) {
                          // Check if the layer exists before trying to update it
                          const layerExists = mapInstance.getLayer(
                            "gl-draw-polygon-stroke-inactive"
                          );
                          if (layerExists) {
                            mapInstance.setPaintProperty(
                              "gl-draw-polygon-stroke-inactive",
                              "line-width",
                              currentWidth
                            );
                          } else {
                            // Layer doesn't exist, clear the interval
                            if (pulseIntervalRef.current) {
                              clearInterval(pulseIntervalRef.current);
                              pulseIntervalRef.current = null;
                            }
                            return;
                          }
                        }
                        pulseCount++;
                      } catch (error) {
                        // If there's an error, clear the interval
                        if (pulseIntervalRef.current) {
                          clearInterval(pulseIntervalRef.current);
                          pulseIntervalRef.current = null;
                        }
                      }
                    } else {
                      if (pulseIntervalRef.current) {
                        clearInterval(pulseIntervalRef.current);
                        pulseIntervalRef.current = null;
                      }
                    }
                  }, 100);

                  return true;
                }
              } catch (error) {
                console.error("Error entering edit mode:", error);
                return false;
              }
              return false;
            };

            const checkAndEnterEditMode = () => {
              try {
                const addedFeatures = drawRef.current?.getAll();
                if (
                  addedFeatures &&
                  addedFeatures.features &&
                  addedFeatures.features.length > 0
                ) {
                  const feature = addedFeatures.features[0];
                  if (feature && feature.id && typeof feature.id === "string") {
                    return true;
                  }
                }
              } catch (error) {
                console.error("Error checking for feature:", error);
              }
              return false;
            };

            // Wait for feature to be added, then enter edit mode with proper timing
            const attemptEnterEditMode = () => {
              if (checkAndEnterEditMode()) {
                const addedFeatures = drawRef.current?.getAll();
                const feature = addedFeatures?.features[0];
                if (feature && feature.id) {
                  // Add a longer delay to ensure the feature is fully rendered
                  setTimeout(() => {
                    enterEditMode(feature.id);
                  }, 200);
                  return true;
                }
              }
              return false;
            };

            // Try immediately
            if (!attemptEnterEditMode()) {
              // If not ready, try again after a short delay
              setTimeout(() => {
                if (!attemptEnterEditMode()) {
                  // Try one more time with longer delay
                  setTimeout(() => {
                    if (!attemptEnterEditMode()) {
                      toast.error("Failed to enter edit mode");
                    }
                  }, 500);
                }
              }, 200);
            }
          } else {
            console.error("Invalid polygon coordinates for editing");
            toast.error("Invalid polygon shape for editing");
          }
        } catch (error) {
          console.error("Error setting up polygon editing:", error);
          toast.error("Failed to load polygon for editing");
        }
      }
    } else {
      // Clear all features from draw tool and reset to neutral mode
      if (drawRef.current) {
        try {
          // Clear any existing pulse interval
          if (pulseIntervalRef.current) {
            clearInterval(pulseIntervalRef.current);
            pulseIntervalRef.current = null;
          }

          // Change to simple_select mode to show hand cursor instead of drawing cursor
          drawRef.current.changeMode("simple_select");

          const features = drawRef.current.getAll();
          if (features && features.features && features.features.length > 0) {
            features.features.forEach((feature: any) => {
              if (feature && feature.id) {
                try {
                  drawRef.current?.delete(feature.id);
                } catch (error) {
                  console.error("Error deleting feature:", error);
                }
              }
            });
          }
        } catch (error) {
          console.error("Error clearing draw features:", error);
        }
      }
    }

    return () => {
      map.off("draw.create", handleDrawCreate);
      map.off("draw.update", handleDrawUpdate);
      map.off("draw.delete", handleDrawDelete);
    };
  }, [
    map,
    drawingMode,
    editingStateId,
    states,
    onStateCreated,
    onStatesEdited,
    onFinishEditing,
    isEditingBorder,
    customBorder,
    onBorderEdited,
  ]);

  return null;
};

// Transform Handles Component
const TransformHandles: React.FC<{
  guideLayer: GuideLayer;
  onTransform: (updates: Partial<GuideLayer>) => void;
  onClose: () => void;
}> = ({ guideLayer, onTransform, onClose }) => {
  const { current: map } = useMap();
  const [isDragging, setIsDragging] = useState(false);
  const [dragType, setDragType] = useState<"move" | "scale" | "rotate" | null>(
    null
  );
  const [startPoint, setStartPoint] = useState<{ x: number; y: number } | null>(
    null
  );
  const [transform, setTransform] = useState({
    offsetX: guideLayer.offsetX || 0,
    offsetY: guideLayer.offsetY || 0,
    scale: guideLayer.scale || 1,
    rotation: guideLayer.rotation || 0,
  });

  // Aspect ratio lock state
  const [aspectRatioLocked, setAspectRatioLocked] = useState(false);
  const [activeHandle, setActiveHandle] = useState<string | null>(null);

  // Calculate the current bounds of the guide layer
  const getGuideLayerBounds = useCallback(() => {
    const scale = transform.scale;
    const offsetX = transform.offsetX;
    const offsetY = transform.offsetY;
    const centerLng = guideLayer.centerLng || 54;
    const centerLat = guideLayer.centerLat || 32.5;
    const width = (guideLayer.width || 20) * scale;
    const height = (guideLayer.height || 20) * scale;

    const halfWidth = width / 2;
    const halfHeight = height / 2;
    const offsetLng = offsetX * 0.01;
    const offsetLat = offsetY * 0.01;

    // Base corners
    const baseCorners = [
      {
        lng: centerLng - halfWidth + offsetLng,
        lat: centerLat - halfHeight + offsetLat,
      }, // SW
      {
        lng: centerLng + halfWidth + offsetLng,
        lat: centerLat - halfHeight + offsetLat,
      }, // SE
      {
        lng: centerLng + halfWidth + offsetLng,
        lat: centerLat + halfHeight + offsetLat,
      }, // NE
      {
        lng: centerLng - halfWidth + offsetLng,
        lat: centerLat + halfHeight + offsetLat,
      }, // NW
    ];

    // Apply rotation if needed
    let corners = baseCorners;
    if (transform.rotation !== 0) {
      const center = { lng: centerLng + offsetLng, lat: centerLat + offsetLat };
      const rad = (transform.rotation * Math.PI) / 180;
      const cos = Math.cos(rad);
      const sin = Math.sin(rad);

      corners = baseCorners.map((corner) => {
        const dx = corner.lng - center.lng;
        const dy = corner.lat - center.lat;
        return {
          lng: center.lng + dx * cos - dy * sin,
          lat: center.lat + dx * sin + dy * cos,
        };
      });
    }

    return {
      corners,
      center: { lng: centerLng + offsetLng, lat: centerLat + offsetLat },
    };
  }, [guideLayer, transform]);

  // Convert geographic coordinates to screen coordinates
  const geoToScreen = useCallback(
    (lng: number, lat: number) => {
      if (!map) return { x: 0, y: 0 };
      try {
        const point = map.project([lng, lat]);
        return { x: point.x, y: point.y };
      } catch (error) {
        console.error("Error converting geo to screen:", error);
        return { x: 0, y: 0 };
      }
    },
    [map]
  );

  // Convert screen coordinates to geographic coordinates
  const screenToGeo = useCallback(
    (x: number, y: number) => {
      if (!map) return { lng: 0, lat: 0 };
      try {
        const point = map.unproject([x, y]);
        return { lng: point.lng, lat: point.lat };
      } catch (error) {
        console.error("Error converting screen to geo:", error);
        return { lng: 0, lat: 0 };
      }
    },
    [map]
  );

  // Handle mouse down on handles
  const handleMouseDown = useCallback(
    (
      e: React.MouseEvent,
      type: "move" | "scale" | "rotate",
      handle?: string
    ) => {
      e.preventDefault();
      e.stopPropagation();

      setIsDragging(true);
      setDragType(type);
      setStartPoint({ x: e.clientX, y: e.clientY });
      setActiveHandle(handle || null);
    },
    []
  );

  // Handle mouse move with aspect ratio lock
  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (!isDragging || !startPoint || !map) return;

      const deltaX = e.clientX - startPoint.x;
      const deltaY = e.clientY - startPoint.y;

      if (dragType === "move") {
        // Convert screen delta to geographic delta
        const startGeo = screenToGeo(startPoint.x, startPoint.y);
        const endGeo = screenToGeo(e.clientX, e.clientY);
        const geoDeltaX = endGeo.lng - startGeo.lng;
        const geoDeltaY = endGeo.lat - startGeo.lat;

        setTransform((prev) => ({
          ...prev,
          offsetX: prev.offsetX + geoDeltaX * 100, // Convert to offset units
          offsetY: prev.offsetY + geoDeltaY * 100,
        }));
      } else if (dragType === "scale") {
        const scaleDelta = deltaX * 0.001;

        if (aspectRatioLocked && activeHandle) {
          // Calculate scale based on the active handle and maintain aspect ratio
          let newScale = transform.scale;

          // Handle-specific scaling logic
          switch (activeHandle) {
            case "nw":
            case "se":
              // Diagonal scaling - use the larger delta
              const diagonalDelta =
                Math.abs(deltaX) > Math.abs(deltaY) ? deltaX : deltaY;
              newScale = Math.max(
                0.1,
                Math.min(5, transform.scale + diagonalDelta * 0.001)
              );
              break;
            case "ne":
            case "sw":
              // Diagonal scaling - use the larger delta
              const diagonalDelta2 =
                Math.abs(deltaX) > Math.abs(deltaY) ? deltaX : deltaY;
              newScale = Math.max(
                0.1,
                Math.min(5, transform.scale + diagonalDelta2 * 0.001)
              );
              break;
            case "n":
            case "s":
              // Vertical scaling - maintain aspect ratio
              newScale = Math.max(
                0.1,
                Math.min(5, transform.scale + deltaY * 0.001)
              );
              break;
            case "e":
            case "w":
              // Horizontal scaling - maintain aspect ratio
              newScale = Math.max(
                0.1,
                Math.min(5, transform.scale + deltaX * 0.001)
              );
              break;
            default:
              // Default scaling
              newScale = Math.max(
                0.1,
                Math.min(5, transform.scale + scaleDelta)
              );
          }

          setTransform((prev) => ({
            ...prev,
            scale: newScale,
          }));
        } else {
          // Free scaling without aspect ratio lock
          setTransform((prev) => ({
            ...prev,
            scale: Math.max(0.1, Math.min(5, prev.scale + scaleDelta)),
          }));
        }
      } else if (dragType === "rotate") {
        const bounds = getGuideLayerBounds();
        const centerScreen = geoToScreen(bounds.center.lng, bounds.center.lat);
        const angle = Math.atan2(
          e.clientY - centerScreen.y,
          e.clientX - centerScreen.x
        );
        const degrees = (angle * 180) / Math.PI;
        setTransform((prev) => ({
          ...prev,
          rotation: degrees,
        }));
      }

      setStartPoint({ x: e.clientX, y: e.clientY });
    },
    [
      isDragging,
      startPoint,
      dragType,
      map,
      screenToGeo,
      geoToScreen,
      getGuideLayerBounds,
      aspectRatioLocked,
      activeHandle,
      transform.scale,
    ]
  );

  // Handle mouse up
  const handleMouseUp = useCallback(() => {
    if (isDragging) {
      onTransform(transform);
    }
    setIsDragging(false);
    setDragType(null);
    setStartPoint(null);
    setActiveHandle(null);
  }, [isDragging, transform, onTransform]);

  // Add/remove global mouse event listeners
  useEffect(() => {
    if (isDragging) {
      document.addEventListener("mousemove", handleMouseMove, {
        passive: true,
      });
      document.addEventListener("mouseup", handleMouseUp);
      return () => {
        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mouseup", handleMouseUp);
      };
    }
  }, [isDragging, handleMouseMove, handleMouseUp]);

  // Handle ESC key to close
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onClose();
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [onClose]);

  // Handle Shift key for aspect ratio lock
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Shift") {
        setAspectRatioLocked(true);
      }
    };
    const handleKeyUp = (e: KeyboardEvent) => {
      if (e.key === "Shift") {
        setAspectRatioLocked(false);
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    document.addEventListener("keyup", handleKeyUp);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.removeEventListener("keyup", handleKeyUp);
    };
  }, []);

  if (!map) {
    return null;
  }

  const bounds = getGuideLayerBounds();
  const centerScreen = geoToScreen(bounds.center.lng, bounds.center.lat);
  const cornerScreens = bounds.corners.map((corner) =>
    geoToScreen(corner.lng, corner.lat)
  );

  return (
    <div
      style={{
        position: "absolute",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        pointerEvents: "none",
      }}
    >
      {/* Border */}
      <svg
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          pointerEvents: "none",
        }}
      >
        <polygon
          points={cornerScreens.map((p) => `${p.x},${p.y}`).join(" ")}
          fill="none"
          stroke="#2196f3"
          strokeWidth="2"
          strokeDasharray="5,5"
        />
      </svg>

      {/* Corner handles for scaling */}
      {cornerScreens.map((corner, index) => {
        const handleNames = ["sw", "se", "ne", "nw"];
        const handleName = handleNames[index];
        const isActive = activeHandle === handleName;

        return (
          <div
            key={`corner-${index}`}
            style={{
              position: "absolute",
              left: corner.x - 8,
              top: corner.y - 8,
              width: 16,
              height: 16,
              backgroundColor: isActive ? "#ff9800" : "#2196f3",
              border: "2px solid white",
              borderRadius: "50%",
              cursor: aspectRatioLocked ? "crosshair" : "nw-resize",
              pointerEvents: "auto",
              zIndex: 1000,
              transition: "all 0.2s ease",
            }}
            onMouseDown={(e) => handleMouseDown(e, "scale", handleName)}
            title={`${handleName.toUpperCase()} handle${
              aspectRatioLocked ? " (Aspect locked)" : ""
            }`}
          />
        );
      })}

      {/* Edge handles for scaling */}
      {(() => {
        const edges = [
          {
            name: "n",
            position: { x: centerScreen.x, y: cornerScreens[3].y - 8 },
          },
          {
            name: "s",
            position: { x: centerScreen.x, y: cornerScreens[0].y + 8 },
          },
          {
            name: "e",
            position: { x: cornerScreens[1].x + 8, y: centerScreen.y },
          },
          {
            name: "w",
            position: { x: cornerScreens[0].x - 8, y: centerScreen.y },
          },
        ];

        return edges.map((edge) => {
          const isActive = activeHandle === edge.name;

          return (
            <div
              key={`edge-${edge.name}`}
              style={{
                position: "absolute",
                left: edge.position.x - 6,
                top: edge.position.y - 6,
                width: 12,
                height: 12,
                backgroundColor: isActive ? "#ff9800" : "#4caf50",
                border: "2px solid white",
                borderRadius: "50%",
                cursor: aspectRatioLocked
                  ? "crosshair"
                  : `${
                      edge.name === "n" || edge.name === "s"
                        ? "ns-resize"
                        : "ew-resize"
                    }`,
                pointerEvents: "auto",
                zIndex: 1000,
                transition: "all 0.2s ease",
              }}
              onMouseDown={(e) => handleMouseDown(e, "scale", edge.name)}
              title={`${edge.name.toUpperCase()} handle${
                aspectRatioLocked ? " (Aspect locked)" : ""
              }`}
            />
          );
        });
      })()}

      {/* Center handle for moving */}
      <div
        style={{
          position: "absolute",
          left: centerScreen.x - 8,
          top: centerScreen.y - 8,
          width: 16,
          height: 16,
          backgroundColor: "#4caf50",
          border: "2px solid white",
          borderRadius: "50%",
          cursor: "move",
          pointerEvents: "auto",
          zIndex: 1000,
        }}
        onMouseDown={(e) => handleMouseDown(e, "move")}
        title="Move handle"
      />

      {/* Rotation handle */}
      <div
        style={{
          position: "absolute",
          left: centerScreen.x - 6,
          top: centerScreen.y - 40,
          width: 12,
          height: 12,
          backgroundColor: "#ff9800",
          border: "2px solid white",
          borderRadius: "50%",
          cursor: "grab",
          pointerEvents: "auto",
          zIndex: 1000,
        }}
        onMouseDown={(e) => handleMouseDown(e, "rotate")}
        title="Rotation handle"
      />

      {/* Instructions */}
      <div
        style={{
          position: "absolute",
          top: "10px",
          right: "10px",
          background: "rgba(0,0,0,0.8)",
          color: "white",
          padding: "10px",
          borderRadius: "4px",
          fontSize: "12px",
          pointerEvents: "none",
          zIndex: 1000,
        }}
      >
        <div style={{ marginBottom: "5px" }}>
          <strong>Transform Controls</strong>
        </div>
        <div style={{ marginBottom: "5px" }}>• Drag green center to move</div>
        <div style={{ marginBottom: "5px" }}>• Drag blue corners to scale</div>
        <div style={{ marginBottom: "5px" }}>
          • Drag orange handle to rotate
        </div>
        <div style={{ marginBottom: "5px" }}>• Press ESC to exit</div>
        <div
          style={{
            marginBottom: "5px",
            fontSize: "10px",
            color: aspectRatioLocked ? "#4caf50" : "#ccc",
            fontWeight: aspectRatioLocked ? "bold" : "normal",
            display: "flex",
            alignItems: "center",
            gap: "4px",
            padding: aspectRatioLocked ? "4px 6px" : "2px 4px",
            background: aspectRatioLocked
              ? "rgba(76, 175, 80, 0.2)"
              : "transparent",
            borderRadius: "3px",
            border: aspectRatioLocked ? "1px solid #4caf50" : "none",
          }}
        >
          <span style={{ fontSize: "12px" }}>
            {aspectRatioLocked ? "🔒" : "💡"}
          </span>
          <span>
            {aspectRatioLocked
              ? "Aspect ratio locked"
              : "Hold Shift to lock aspect ratio"}
          </span>
        </div>
        <div style={{ marginBottom: "5px", fontSize: "10px", color: "#ccc" }}>
          Debug:{" "}
          {isDragging
            ? `Dragging ${dragType}${activeHandle ? ` (${activeHandle})` : ""}`
            : "Not dragging"}
        </div>
      </div>
    </div>
  );
};

const MapComponent = forwardRef<MapComponentRef, MapComponentProps>(
  (
    {
      states,
      guideLayers,
      drawingMode,
      selectedState,
      editingStateId,
      showEditingInstructions,
      onStateCreated,
      onStateSelected,
      onStatesEdited,
      onFinishEditing,
      onHideInstructions,
      showCapitals,
      showCapitalNames,
      showStateNames,
      showStateFill,
      mapStyle,
      onCapitalCoordinateSet,
      isSettingCapital,
      editingGuideLayerId,
      selectedGuideLayer,
      onGuideLayerTransform,
      setDrawingMode,
      regionCode,
      showBorder,
      customBorder,
      isEditingBorder,
      onBorderEdited,
    },
    ref
  ) => {
    const mapRef = useRef<any>(null);
    const [viewState, setViewState] = useState({
      longitude: 54.0,
      latitude: 32.5,
      zoom: 6,
    });

    useImperativeHandle(ref, () => ({
      captureMap: async (): Promise<HTMLCanvasElement> => {
        if (!mapRef.current) {
          throw new Error("Map not initialized");
        }

        const map = mapRef.current.getMap();

        // Check if map is in a valid state
        if (!map || !map.getCanvas) {
          throw new Error("Map is not properly initialized");
        }

        // Wait for map to be fully loaded with better error handling
        await new Promise<void>((resolve, reject) => {
          const timeout = setTimeout(() => {
            reject(new Error("Map loading timeout"));
          }, 10000); // 10 second timeout

          try {
            // Check if map is already idle
            if (map.isStyleLoaded() && map.loaded()) {
              clearTimeout(timeout);
              setTimeout(resolve, 100);
              return;
            }

            // Use the idle event which fires when the map is fully loaded and rendered
            map.once("idle", () => {
              clearTimeout(timeout);
              // Add a small delay to ensure everything is rendered
              setTimeout(resolve, 100);
            });
          } catch (error) {
            clearTimeout(timeout);
            reject(new Error(`Map loading error: ${error}`));
          }
        });

        // Get the canvas with better error handling
        let canvas: HTMLCanvasElement | undefined;
        try {
          // Wait a bit more to ensure the map is fully rendered
          await new Promise((resolve) => setTimeout(resolve, 500));

          // Try to get the canvas multiple times if needed
          let attempts = 0;
          const maxAttempts = 5;

          while (attempts < maxAttempts) {
            try {
              canvas = map.getCanvas();

              if (canvas && canvas.width > 0 && canvas.height > 0) {
                break;
              }
            } catch (e) {
              // Canvas not ready yet, continue trying
            }

            attempts++;
            if (attempts < maxAttempts) {
              await new Promise((resolve) => setTimeout(resolve, 200));
            }
          }

          if (!canvas || canvas.width === 0 || canvas.height === 0) {
            throw new Error(
              "Map canvas is not ready or has invalid dimensions"
            );
          }
        } catch (error) {
          throw new Error(`Failed to get map canvas: ${error}`);
        }

        // Create a new canvas for the final result
        const finalCanvas = document.createElement("canvas");
        finalCanvas.width = canvas.width;
        finalCanvas.height = canvas.height;

        let finalCtx: CanvasRenderingContext2D | null;
        try {
          finalCtx = finalCanvas.getContext("2d");
        } catch (error) {
          throw new Error(`Failed to get canvas context: ${error}`);
        }

        if (!finalCtx) {
          throw new Error("Failed to get canvas context");
        }

        // Draw the map canvas onto the final canvas
        finalCtx.drawImage(canvas, 0, 0);

        return finalCanvas;
      },
      resize: () => {
        if (mapRef.current) {
          const map = mapRef.current.getMap();
          if (map && map.resize) {
            map.resize();
          }
        }
      },
      flyTo: (options: {
        center: [number, number];
        zoom: number;
        duration?: number;
      }) => {
        if (mapRef.current) {
          const map = mapRef.current.getMap();
          if (map && map.flyTo) {
            map.flyTo(options);
          }
        }
      },
    }));

    // Handle map click for capital setting and guide layer selection
    const handleMapClick = useCallback(
      (event: any) => {
        if (!mapRef.current) return;

        const { lngLat } = event;
        const coordinates: Coordinates = {
          lng: lngLat.lng,
          lat: lngLat.lat,
        };

        // Handle capital setting
        if (selectedState && isSettingCapital && onCapitalCoordinateSet) {
          // Check if point is inside state polygon
          if (
            selectedState.coordinates &&
            selectedState.coordinates.length > 0
          ) {
            const polygonCoords = selectedState.coordinates[0];
            if (isPointInPolygon(coordinates, polygonCoords)) {
              onCapitalCoordinateSet(selectedState.id, coordinates);
              toast.success("Capital pin placed successfully!");
            } else {
              toast.error(
                "Capital pin must be placed inside the state boundary"
              );
            }
          } else {
            onCapitalCoordinateSet(selectedState.id, coordinates);
            toast.success("Capital pin placed successfully!");
          }
          return;
        }

        // Handle state selection - exit drawing mode and select state when clicked
        if (!isSettingCapital && !editingStateId && !editingGuideLayerId) {
          // Find which state was clicked
          const clickedState = states.find((state) => {
            if (!state.coordinates || state.coordinates.length === 0)
              return false;
            return isPointInPolygon(coordinates, state.coordinates[0]);
          });

          if (clickedState) {
            // If in drawing mode, exit it first
            if (drawingMode && setDrawingMode) {
              setDrawingMode(false);
            }
            onStateSelected(clickedState);
          }
        }
      },
      [
        selectedState,
        isSettingCapital,
        onCapitalCoordinateSet,
        drawingMode,
        editingStateId,
        editingGuideLayerId,
        states,
        onStateSelected,
        setDrawingMode,
      ]
    );

    // Handle guide layer transformations
    const handleGuideLayerTransform = useCallback(
      (updates: Partial<GuideLayer>) => {
        if (selectedGuideLayer && onGuideLayerTransform) {
          onGuideLayerTransform(selectedGuideLayer.id, updates);
        }
      },
      [selectedGuideLayer, onGuideLayerTransform]
    );

    // Handle closing transform mode
    const handleCloseTransform = useCallback(() => {
      // This will be handled by the StatePanel when the guide layer is deselected
    }, []);

    // Filter out any legacy border states (borders are now handled separately)
    const regularStates = useMemo(() => {
      const filtered = states.filter((s) => !s.isBorder);
      return filtered;
    }, [states]);

    // Generate GeoJSON for regular states (non-border)
    const statesGeoJSON = useMemo(
      () => ({
        type: "FeatureCollection" as const,
        features: regularStates.map((state) => ({
          type: "Feature" as const,
          properties: {
            id: state.id,
            name: state.name,
            color: state.color,
            selected: selectedState?.id === state.id,
          },
          geometry: {
            type: "Polygon" as const,
            coordinates: [
              state.coordinates[0].map((coord) => [coord.lng, coord.lat]),
            ],
          },
        })),
      }),
      [regularStates, selectedState]
    );

    // Generate GeoJSON for capitals (exclude border states)
    const capitalsGeoJSON = useMemo(
      () => ({
        type: "FeatureCollection" as const,
        features: regularStates
          .filter(
            (state) =>
              showCapitals &&
              (state.capitalCoordinates || state.coordinates.length > 0)
          )
          .map((state) => {
            const position =
              state.capitalCoordinates || calculateCentroid(state.coordinates);
            return {
              type: "Feature" as const,
              properties: {
                id: state.id,
                name: state.capital || state.name,
                color: state.color,
              },
              geometry: {
                type: "Point" as const,
                coordinates: [position.lng, position.lat],
              },
            };
          }),
      }),
      [regularStates, showCapitals]
    );

    // Generate GeoJSON for capital name labels (exclude border states)
    const capitalNamesGeoJSON = useMemo(
      () => ({
        type: "FeatureCollection" as const,
        features: regularStates
          .filter(
            (state) =>
              showCapitalNames &&
              state.capital &&
              (state.capitalCoordinates || state.coordinates.length > 0)
          )
          .map((state) => {
            const position =
              state.capitalCoordinates || calculateCentroid(state.coordinates);
            return {
              type: "Feature" as const,
              properties: {
                id: state.id,
                name: state.capital,
                color: state.color,
              },
              geometry: {
                type: "Point" as const,
                coordinates: [position.lng, position.lat],
              },
            };
          }),
      }),
      [regularStates, showCapitalNames]
    );

    // Generate GeoJSON for state name labels (exclude border states)
    const stateNamesGeoJSON = useMemo(
      () => ({
        type: "FeatureCollection" as const,
        features: regularStates
          .filter((state) => showStateNames && state.coordinates.length > 0)
          .map((state) => {
            const centroid = calculateCentroid(state.coordinates);
            return {
              type: "Feature" as const,
              properties: {
                id: state.id,
                name: state.name,
                color: state.color,
              },
              geometry: {
                type: "Point" as const,
                coordinates: [centroid.lng, centroid.lat],
              },
            };
          }),
      }),
      [regularStates, showStateNames]
    );

    // Set projection to globe for satellite style, mercator for others
    const projection = useMemo(() => {
      return mapStyle === "satellite"
        ? { name: "globe" as const }
        : { name: "mercator" as const };
    }, [mapStyle]);

    // Border GeoJSON (custom or region border)
    const borderGeoJSON = useMemo(() => {
      // Don't show border if showBorder is false or if editing border via MapBox Draw
      if (showBorder === false || isEditingBorder) {
        return null;
      }

      // Use custom border if available
      if (customBorder && customBorder.length > 0) {
        return {
          type: "FeatureCollection",
          features: customBorder.map((ring, index) => ({
            type: "Feature",
            properties: { id: `custom-border-${index}`, type: "custom-border" },
            geometry: {
              type: "Polygon",
              coordinates: [ring.map((coord) => [coord.lng, coord.lat])],
            },
          })),
        } as GeoJSON.FeatureCollection;
      }

      // Fall back to region border
      const border = regionCode
        ? getRegionBorder(regionCode as RegionCode)
        : null;
      if (!border) {
        return null;
      }

      return {
        type: "FeatureCollection",
        features: [
          {
            type: "Feature",
            properties: border.properties,
            geometry: border.geometry,
          },
        ],
      } as GeoJSON.FeatureCollection;
    }, [regionCode, showBorder, customBorder, isEditingBorder]);

    return (
      <div style={{ position: "relative", width: "100%", height: "100%" }}>
        <Map
          ref={mapRef}
          {...viewState}
          onMove={(evt: ViewStateChangeEvent) => setViewState(evt.viewState)}
          style={{ width: "100%", height: "100%" }}
          mapStyle={mapStyle ? MAP_STYLES[mapStyle].url : MAP_STYLES.light.url}
          mapboxAccessToken={MAPBOX_ACCESS_TOKEN}
          onClick={handleMapClick}
          projection={projection}
          interactiveLayerIds={[
            "states-fill",
            "states-border",
            "selected-state-fill",
            "capitals",
          ]}
        >
          <NavigationControl position="top-right" />
          <ScaleControl position="bottom-left" />

          {/* Drawing Controls */}
          <DrawingControls
            drawingMode={drawingMode}
            editingStateId={editingStateId}
            states={states}
            onStateCreated={onStateCreated}
            onStatesEdited={onStatesEdited}
            onFinishEditing={onFinishEditing}
            isEditingBorder={isEditingBorder}
            customBorder={customBorder}
            onBorderEdited={onBorderEdited}
          />

          {/* Region Border (static visual layer) */}
          {borderGeoJSON && (
            <Source type="geojson" data={borderGeoJSON}>
              <Layer
                id="region-border"
                type="line"
                paint={{
                  "line-color": "#333333",
                  "line-width": 2.5,
                  "line-dasharray": [2, 1],
                }}
                layout={{
                  "line-cap": "round",
                  "line-join": "round",
                }}
              />
            </Source>
          )}

          {/* States */}
          <Source type="geojson" data={statesGeoJSON}>
            <Layer
              id="states-fill"
              type="fill"
              paint={{
                "fill-color": ["get", "color"],
                "fill-opacity": showStateFill !== false ? 0.3 : 0,
              }}
              filter={["!=", ["get", "selected"], true]}
            />
            <Layer
              id="states-border"
              type="line"
              paint={{
                "line-color": ["get", "color"],
                "line-width": 2,
              }}
            />
            <Layer
              id="selected-state-fill"
              type="fill"
              paint={{
                "fill-color": ["get", "color"],
                "fill-opacity": showStateFill !== false ? 0.5 : 0,
              }}
              filter={["==", ["get", "selected"], true]}
            />
          </Source>

          {/* Capitals */}
          {showCapitals && (
            <Source type="geojson" data={capitalsGeoJSON}>
              <Layer
                id="capitals"
                type="circle"
                paint={{
                  "circle-radius": 8,
                  "circle-color": ["get", "color"],
                  "circle-stroke-color": "#ffffff",
                  "circle-stroke-width": 2,
                }}
              />
            </Source>
          )}

          {/* Capital Name Labels */}
          {showCapitalNames && (
            <Source type="geojson" data={capitalNamesGeoJSON}>
              <Layer
                id="capital-names"
                type="symbol"
                layout={{
                  "text-field": ["get", "name"],
                  "text-font": ["Open Sans Semibold", "Arial Unicode MS Bold"],
                  "text-size": 12,
                  "text-offset": [0, 1.5],
                  "text-anchor": "top",
                }}
                paint={{
                  "text-color": ["get", "color"],
                  "text-halo-color": "#ffffff",
                  "text-halo-width": 2,
                }}
              />
            </Source>
          )}

          {/* State Name Labels */}
          {showStateNames && (
            <Source type="geojson" data={stateNamesGeoJSON}>
              <Layer
                id="state-names"
                type="symbol"
                layout={{
                  "text-field": ["get", "name"],
                  "text-font": ["Open Sans Semibold", "Arial Unicode MS Bold"],
                  "text-size": 14,
                  "text-anchor": "center",
                }}
                paint={{
                  "text-color": ["get", "color"],
                  "text-halo-color": "#ffffff",
                  "text-halo-width": 2,
                }}
              />
            </Source>
          )}

          {/* Guide Layers */}
          {guideLayers
            .filter((layer) => layer.visible)
            .map((guideLayer) => {
              // Calculate scaled and offset coordinates
              const scale = guideLayer.scale || 1;
              const offsetX = guideLayer.offsetX || 0;
              const offsetY = guideLayer.offsetY || 0;
              const rotation = guideLayer.rotation || 0;
              const centerLng = guideLayer.centerLng || 54;
              const centerLat = guideLayer.centerLat || 32.5;
              const width = (guideLayer.width || 20) * scale;
              const height = (guideLayer.height || 20) * scale;

              const halfWidth = width / 2;
              const halfHeight = height / 2;

              // Apply offset
              const offsetLng = offsetX * 0.01;
              const offsetLat = offsetY * 0.01;

              // Base corners
              const baseCorners = [
                {
                  lng: centerLng - halfWidth + offsetLng,
                  lat: centerLat - halfHeight + offsetLat,
                }, // SW
                {
                  lng: centerLng + halfWidth + offsetLng,
                  lat: centerLat - halfHeight + offsetLat,
                }, // SE
                {
                  lng: centerLng + halfWidth + offsetLng,
                  lat: centerLat + halfHeight + offsetLat,
                }, // NE
                {
                  lng: centerLng - halfWidth + offsetLng,
                  lat: centerLat + halfHeight + offsetLat,
                }, // NW
              ];

              // Apply rotation if needed
              let corners = baseCorners;
              if (rotation !== 0) {
                const center = {
                  lng: centerLng + offsetLng,
                  lat: centerLat + offsetLat,
                };
                const rad = (rotation * Math.PI) / 180;
                const cos = Math.cos(rad);
                const sin = Math.sin(rad);

                corners = baseCorners.map((corner) => {
                  const dx = corner.lng - center.lng;
                  const dy = corner.lat - center.lat;
                  return {
                    lng: center.lng + dx * cos - dy * sin,
                    lat: center.lat + dx * sin + dy * cos,
                  };
                });
              }

              return (
                <Source
                  key={guideLayer.id}
                  type="image"
                  url={guideLayer.imageUrl}
                  coordinates={[
                    [corners[3].lng, corners[3].lat], // NW (top-left)
                    [corners[2].lng, corners[2].lat], // NE (top-right)
                    [corners[1].lng, corners[1].lat], // SE (bottom-right)
                    [corners[0].lng, corners[0].lat], // SW (bottom-left)
                  ]}
                >
                  <Layer
                    id={`guide-layer-${guideLayer.id}`}
                    type="raster"
                    paint={{
                      "raster-opacity": guideLayer.opacity,
                    }}
                  />
                </Source>
              );
            })}

          {/* Transform Handles (must be inside <Map> for useMap to work) */}
          {selectedGuideLayer && (
            <TransformHandles
              guideLayer={selectedGuideLayer}
              onTransform={handleGuideLayerTransform}
              onClose={handleCloseTransform}
            />
          )}
        </Map>

        {/* Drawing Mode Indicator */}
        {drawingMode && (
          <div
            style={{
              position: "absolute",
              top: "10px",
              left: "10px",
              background: "rgba(0,0,0,0.8)",
              color: "white",
              padding: "10px",
              borderRadius: "4px",
              fontSize: "12px",
              zIndex: 1000,
            }}
          >
            <div style={{ marginBottom: "5px" }}>
              <strong>Drawing Mode Active</strong>
            </div>
            <div style={{ marginBottom: "5px" }}>
              Click to add points, double-click to finish polygon.
            </div>
            <div style={{ fontSize: "11px", opacity: 0.8 }}>
              Move mouse to see preview line from last vertex.
            </div>
          </div>
        )}

        {/* Editing Mode Indicator */}
        {editingStateId && !drawingMode && showEditingInstructions && (
          <div
            style={{
              position: "absolute",
              top: "10px",
              left: "10px",
              background: "rgba(0,0,0,0.9)",
              color: "white",
              padding: "12px",
              borderRadius: "6px",
              fontSize: "12px",
              zIndex: 1000,
              maxWidth: "320px",
              border: "2px solid #ff6b35",
              boxShadow: "0 4px 12px rgba(255, 107, 53, 0.3)",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "8px",
                color: "#ff6b35",
                fontWeight: "bold",
              }}
            >
              <span>✏️ Editing Mode Active</span>
              <button
                onClick={onHideInstructions}
                style={{
                  background: "transparent",
                  border: "none",
                  color: "#ff6b35",
                  fontSize: "16px",
                  cursor: "pointer",
                  padding: "0",
                  marginLeft: "8px",
                  fontWeight: "bold",
                  opacity: 0.8,
                  transition: "opacity 0.2s",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.opacity = "1";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.opacity = "0.8";
                }}
                title="Hide instructions"
              >
                ×
              </button>
            </div>
            <div style={{ marginBottom: "6px", color: "#ffd700" }}>
              • Click on the <strong>orange dashed polygon</strong> to activate
              editing mode
            </div>
            <div style={{ marginBottom: "6px" }}>
              • Drag{" "}
              <strong style={{ color: "#ff6b35" }}>orange vertices</strong> to
              reshape the polygon
            </div>
            <div style={{ marginBottom: "6px" }}>
              • Click{" "}
              <strong style={{ color: "#ff6b35" }}>orange midpoints</strong> to
              add new vertices
            </div>
            <div style={{ marginBottom: "6px" }}>
              • Click "Finish Editing" next to the layer to save changes
            </div>
          </div>
        )}

        {/* Guide Layer Transform Mode Indicator */}
      </div>
    );
  }
);

// Set the display name
(MapComponent as any).displayName = "MapComponent";

export default MapComponent;
